"use client";

import React, { useState, useRef, useCallback } from "react";
import { PiCamera, PiLink, PiX, PiCheck, PiSpinner, PiUploadSimple, PiImage } from "react-icons/pi";
import homepageApi from "@/services/homepageApi";
import toast from "react-hot-toast";
import { getImageUrl } from "@/utils/image-url";
import logger from "@/utils/logger";

// ============================================================
// LOGO UPLOADER COMPONENT
// ============================================================
// A professional, reusable component for logo display and editing
// Supports:
// - Dynamic logo from subdomain/owner
// - File upload via uploadLogo API
// - URL input for external logos
// ============================================================

interface LogoUploaderProps {
    /** Current logo URL from content */
    currentLogo?: string;
    /** Default logo from subdomain/owner (fallback) */
    defaultLogo?: string;
    /** Callback when logo changes */
    onLogoChange: (newLogoUrl: string) => void;
    /** Logo height class */
    heightClass?: string;
    /** Primary color for styling */
    primaryColor?: string;
    /** Secondary color for gradient */
    secondaryColor?: string;
    /** Company name fallback text */
    companyName?: string;
    /** Whether editing is enabled */
    isEditing?: boolean;
}

export default function LogoUploader({
    currentLogo,
    defaultLogo,
    onLogoChange,
    heightClass = "h-10",
    primaryColor = "#3B82F6",
    secondaryColor = "#4F46E5",
    companyName = "INSTACKS",
    isEditing = true,
}: LogoUploaderProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [activeTab, setActiveTab] = useState<"upload" | "url">("upload");
    const [urlInput, setUrlInput] = useState("");
    const [isUploading, setIsUploading] = useState(false);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const popoverRef = useRef<HTMLDivElement>(null);

    // Determine the final logo source
    const getFinalLogoSrc = useCallback(() => {
        // Priority: content.logo > defaultLogo (subdomain) > system fallback
        if (currentLogo && isValidImageUrl(currentLogo)) {
            return getImageUrl(currentLogo) || currentLogo;
        }
        if (defaultLogo && isValidImageUrl(defaultLogo)) {
            return getImageUrl(defaultLogo) || defaultLogo;
        }
        return "/instacks-logo.png";
    }, [currentLogo, defaultLogo]);

    const finalLogoSrc = getFinalLogoSrc();

    // Check if a string is a valid image URL
    function isValidImageUrl(url: string): boolean {
        if (!url) return false;
        return (
            url.startsWith("http") ||
            url.startsWith("data:") ||
            url.startsWith("/") ||
            url.startsWith("blob:") ||
            url.length > 50 // Base64 or long URL
        );
    }

    // Handle file selection
    const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith("image/")) {
            toast.error("Please select an image file");
            return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            toast.error("Logo must be less than 5MB");
            return;
        }

        // Create preview
        const objectUrl = URL.createObjectURL(file);
        setPreviewUrl(objectUrl);

        // Upload the file
        setIsUploading(true);
        try {
            const response = await homepageApi.uploadLogo(file);

            if (response.success && response.data?.logo) {
                onLogoChange(response.data.logo);
                toast.success("Logo uploaded successfully!");
                setIsOpen(false);
            } else {
                throw new Error(response.message || "Upload failed");
            }
        } catch (error: any) {
            logger.error("Logo upload error:", error);
            toast.error(error.message || "Failed to upload logo");
            setPreviewUrl(null);
        } finally {
            setIsUploading(false);
            // Clean up object URL
            URL.revokeObjectURL(objectUrl);
        }
    };

    // Handle URL submission
    const handleUrlSubmit = () => {
        if (!urlInput.trim()) {
            toast.error("Please enter a URL");
            return;
        }

        if (!isValidImageUrl(urlInput.trim())) {
            toast.error("Please enter a valid image URL");
            return;
        }

        onLogoChange(urlInput.trim());
        toast.success("Logo updated!");
        setUrlInput("");
        setIsOpen(false);
    };

    // Handle click outside to close
    React.useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen]);

    // Render the logo image
    const renderLogoImage = () => (
        <img
            src={finalLogoSrc}
            alt="Logo"
            className={`${heightClass} w-auto object-contain transition-opacity ${isEditing ? "cursor-pointer hover:opacity-80" : ""}`}
            onClick={() => isEditing && setIsOpen(true)}
            onError={(e) => {
                (e.target as HTMLImageElement).src = "/instacks-logo.png";
            }}
        />
    );

    // Render text fallback (when no image)
    const renderTextLogo = () => (
        <span
            className={`text-2xl font-bold bg-clip-text text-transparent tracking-tight ${isEditing ? "cursor-pointer hover:opacity-80" : ""}`}
            style={{
                backgroundImage: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`,
            }}
            onClick={() => isEditing && setIsOpen(true)}
        >
            {companyName}
        </span>
    );

    return (
        <div className="relative inline-block">
            {/* Logo Display */}
            <div className="relative group">
                {isValidImageUrl(finalLogoSrc) ? renderLogoImage() : renderTextLogo()}

                {/* Edit Overlay (only in editing mode) */}
                {isEditing && (
                    <div
                        className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded cursor-pointer"
                        onClick={() => setIsOpen(true)}
                    >
                        <PiCamera className="w-5 h-5 text-white" />
                    </div>
                )}
            </div>

            {/* Logo Editor Popover */}
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />

                    {/* Popover */}
                    <div
                        ref={popoverRef}
                        className="absolute left-0 top-full mt-2 z-50 w-80 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200"
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-indigo-50">
                            <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                                <PiImage className="w-5 h-5 text-blue-600" />
                                Update Logo
                            </h3>
                            <button
                                onClick={() => setIsOpen(false)}
                                className="p-1 hover:bg-gray-200/50 rounded-full transition-colors"
                            >
                                <PiX className="w-5 h-5 text-gray-500" />
                            </button>
                        </div>

                        {/* Tab Switcher */}
                        <div className="flex border-b border-gray-100">
                            <button
                                onClick={() => setActiveTab("upload")}
                                className={`flex-1 py-2.5 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${activeTab === "upload"
                                        ? "text-blue-600 border-b-2 border-blue-600 bg-blue-50/50"
                                        : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                                    }`}
                            >
                                <PiUploadSimple className="w-4 h-4" />
                                Upload
                            </button>
                            <button
                                onClick={() => setActiveTab("url")}
                                className={`flex-1 py-2.5 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${activeTab === "url"
                                        ? "text-blue-600 border-b-2 border-blue-600 bg-blue-50/50"
                                        : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                                    }`}
                            >
                                <PiLink className="w-4 h-4" />
                                URL
                            </button>
                        </div>

                        {/* Content */}
                        <div className="p-4">
                            {activeTab === "upload" ? (
                                <div className="space-y-3">
                                    {/* Upload Area */}
                                    <div
                                        onClick={() => !isUploading && fileInputRef.current?.click()}
                                        className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all ${isUploading
                                                ? "border-blue-300 bg-blue-50"
                                                : "border-gray-200 hover:border-blue-400 hover:bg-blue-50/30"
                                            }`}
                                    >
                                        {isUploading ? (
                                            <div className="flex flex-col items-center gap-2">
                                                <PiSpinner className="w-8 h-8 text-blue-600 animate-spin" />
                                                <span className="text-sm text-blue-600 font-medium">Uploading...</span>
                                            </div>
                                        ) : previewUrl ? (
                                            <img
                                                src={previewUrl}
                                                alt="Preview"
                                                className="h-16 mx-auto object-contain rounded"
                                            />
                                        ) : (
                                            <>
                                                <PiUploadSimple className="w-10 h-10 mx-auto text-gray-400 mb-2" />
                                                <p className="text-sm text-gray-600 font-medium">
                                                    Click to upload logo
                                                </p>
                                                <p className="text-xs text-gray-400 mt-1">
                                                    PNG, JPG, SVG up to 5MB
                                                </p>
                                            </>
                                        )}
                                    </div>

                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/*"
                                        onChange={handleFileSelect}
                                        className="hidden"
                                    />

                                    {/* Current Logo Preview */}
                                    {finalLogoSrc && finalLogoSrc !== "/instacks-logo.png" && (
                                        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                                            <img
                                                src={finalLogoSrc}
                                                alt="Current"
                                                className="h-8 w-auto object-contain rounded"
                                            />
                                            <span className="text-xs text-gray-500 flex-1 truncate">
                                                Current logo
                                            </span>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {/* URL Input */}
                                    <div className="relative">
                                        <PiLink className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                        <input
                                            type="url"
                                            value={urlInput}
                                            onChange={(e) => setUrlInput(e.target.value)}
                                            placeholder="https://example.com/logo.png"
                                            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            onKeyDown={(e) => e.key === "Enter" && handleUrlSubmit()}
                                        />
                                    </div>

                                    {/* Submit Button */}
                                    <button
                                        onClick={handleUrlSubmit}
                                        disabled={!urlInput.trim()}
                                        className="w-full py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg font-medium text-sm hover:from-blue-700 hover:to-indigo-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                    >
                                        <PiCheck className="w-4 h-4" />
                                        Apply Logo
                                    </button>

                                    {/* URL Preview */}
                                    {urlInput && isValidImageUrl(urlInput) && (
                                        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                                            <img
                                                src={urlInput}
                                                alt="Preview"
                                                className="h-8 w-auto object-contain rounded"
                                                onError={(e) => {
                                                    (e.target as HTMLImageElement).style.display = "none";
                                                }}
                                            />
                                            <span className="text-xs text-gray-500">Preview</span>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="px-4 py-3 bg-gray-50 border-t border-gray-100">
                            <p className="text-xs text-gray-500 text-center">
                                💡 Upload your organization's logo for a personalized experience
                            </p>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
